import { createNestedNodeId, createNetNode, createSystemNode, createTUID, filterOrphanedNodes, getSubTreeUids, Net, PcbNodeTypes, resetChildUidsAndRootNode, } from "@buildwithflux/models";
import { asMutable, hasWebWorkerContext, isDevEnv, keySort, Records } from "@buildwithflux/shared";
import { chain, cloneDeep, compact, isEqual, mapValues, omit, pick, pickBy, sortBy } from "lodash";
import { isDraft } from "mutative";
import { diffNetMaps, generateNetInstanceMap } from "../../document/functions";
import { addNet, addPinsToNet, getNetHashMap } from "../../document/models/net";
import { MissingChildUidError } from "../../flux_error";
import { createNetDesignatorSequencer, deleteNetsExcludingDescendants, getNetName, mergeNets, splitNets, } from "../../nets";
import { addPcbNodes, getChildByName, getDescendantsOfType, getFirstTopLevelContainerNode, isTopLevelNode, PcbNodeHelper, removeNodesAndDescendants, removeOrphanedSegments, } from "../../nodes";
import { reParentSegments } from "../libs/reparentRouteSegments";
/**
 * Please checkout this [Coda doc](https://coda.io/d/Infrastructure_dK_rvJRCUMg/PCB-Layout_suXGB#_luDnX)
 * to understand how PcbLayout works.
 *
 * Also see more recent:
 * https://coda.io/d/North-Star-Architecture-Notes_dH-26nnEKbE/Pcb-Layout-Discussion_suBvS#_luGa_
 *
 * PcbTreeManager makes a DEEP COPY of treeNodes so they can be mutated safely,
 * unless a draft it passed in or we are in a web worker.
 *
 * TODO: [NodeType] - Make this class accept generic type about what node (baked OR unbaked) it
 * will be processing. Then we can remove many as cast when using `pcbTreeManager.treeNodes` AND
 * any related function calls
 */
export class PcbTreeManager {
    logger;
    // BIG QUESTION: What nodes are expected in this class? Or we need to make it generic
    treeNodes;
    constructor(treeNodes, logger, dangerouslyMutateInPlace = false) {
        this.logger = logger;
        // Previously, we used ImmerJS createDraft here to keep treeNodes
        // immutable. However, we found the Immer proxy object added too much
        // overhead. In treeTraveler, we perform iterations over all treeNodes.
        // With cloneDeep, we pay the price of iteration only once. In addition,
        // when PcbLayoutEngine is used in a web worker, object identity of the
        // returned unchanged nodes cannot be maintained, which in turn
        // precludes efficient memoization in store selectors. See
        // https://github.com/immerjs/immer/issues/619 for more on cloneDeep vs
        // draft.
        //
        // When the input is already a draft, we leave it as such because
        // callers could be depdending on the mutation in place. When we are in
        // a web worker, we are already working on a copy of the data, so we
        // leave it alone.
        this.treeNodes =
            isDraft(treeNodes) || hasWebWorkerContext() || dangerouslyMutateInPlace ? treeNodes : cloneDeep(treeNodes);
    }
    /**
     * Returns a subset of the tree managers tree nodes that are unbaked.
     * The map key is a node's unique id.
     */
    getUnbakedNodes() {
        // TODO: consider caching to avoid pickBy
        return pickBy(this.treeNodes, (node) => !PcbNodeHelper.isBakedNode(node)); // TODO: fix 'as' casting
    }
    /**
     * Returns a subset of the tree managers tree nodes that are baked.
     * The map key is a node's unique id.
     */
    getBakedNodes() {
        // TODO: consider caching to avoid pickBy
        return pickBy(this.treeNodes, (node) => PcbNodeHelper.isBakedNode(node)); // TODO: fix 'as' casting
    }
    /**
     * Adds nodes to the tree. Encapsulates tree mutation.
     * @param nodes - Nodes to add to the tree
     */
    addNodes(nodes) {
        addPcbNodes(this.treeNodes, nodes);
    }
    /**
     * Removes nodes and their descendants from the tree. Encapsulates tree mutation.
     * @param nodeUids - UIDs of nodes to remove
     */
    removeNodesByUid(nodeUids) {
        removeNodesAndDescendants(this.treeNodes, nodeUids);
    }
    /**
     * Updates nodes in the tree. Encapsulates tree mutation.
     * @param nodes - Nodes to update (must already exist in tree)
     * @throws Error if attempting to change node type or uid
     */
    updateNodes(nodes) {
        for (const node of nodes) {
            const existingNode = this.treeNodes[node.uid];
            if (!existingNode) {
                continue; // Node doesn't exist, skip
            }
            // Safeguard: prevent changing type or uid
            if (node.type !== existingNode.type) {
                throw new Error(`Cannot change node type from ${existingNode.type} to ${node.type} for node ${node.uid}`);
            }
            if (node.uid !== existingNode.uid) {
                throw new Error(`Cannot change node uid (attempted to change ${existingNode.uid} to ${node.uid})`);
            }
            // Safe to update
            this.treeNodes[node.uid] = node;
        }
    }
    updatePcbLayoutNode(nodeUid, updateCallback) {
        // TODO: [NodeType] - remove `as` cast here, it should be safe to do this
        // as cast for now because we always update bakedRules here. But better to not have it
        const node = this.treeNodes[nodeUid];
        updateCallback(node);
        return node;
    }
    /**
     * Clones the pcbLayoutNodes for an element, and scope their uids
     */
    createScopedNodesFromElement(element) {
        const elementNodes = filterOrphanedNodes(Object.values(cloneDeep(element.part_version_data_cache.pcbLayoutNodes || {})));
        const result = Object.fromEntries(Object.entries(elementNodes).map(([_key, node]) => {
            const nodeClone = cloneDeep(node);
            // TODO: We are creating a new uid for nodes in sub-layout because node data
            // is de-normalized. This is a technical debt we need to resolve because
            // `uid` should be immutable. We dont need to do all this when
            // we migrate to use snapshot later
            // scope the uid
            asMutable(nodeClone).uid = createNestedNodeId(nodeClone.uid, element.uid);
            if (!isTopLevelNode(nodeClone)) {
                // assuming this is not the root node, scope the parentUid
                nodeClone.parentUid = createNestedNodeId(nodeClone.parentUid, element.uid);
            }
            else {
                nodeClone.parentUid = element.uid;
            }
            return [nodeClone.uid, nodeClone];
        }));
        /**
         * `computeChildUids` is needed here beause nodes from `part_version_data_cache`
         * have no childUids. We only computeChildUids on load for all existing nodes,
         * but not when working on elements sub-layouts, for example, when adding elements
         *
         * See more description in https://github.com/buildwithflux/flux-app/pull/5062
         */
        return resetChildUidsAndRootNode(result);
    }
    /**
     * This function performs several operations to finish updating net nodes, including add, delete,
     * reparent and removeOrphanedSegments. As a result, this function is getting called in various scenarios.
     *
     * TODO: We should really break down this function to smaller units with limited responsibility and call
     * them individually as needed.
     * TODO: Move this to domain function
     *
     * @param document - existing document. Will be mutated.
     * @param deleteNodesOnly - pass true when only using this function to delete net nodes.
     */
    updateNetNodes(document, opts = {}) {
        /**
         * We do not execute this function when we are calling this function
         * to only delete nodes, and there's no vertices at all (i.e. no nets needed).
         * In this case, if we executed, we will end up with one single "Nets"
         * node when user didn't do anything with nets
         *
         * When `deleteNodesOnly` is true, we will execute this function and delete
         * all net nodes. `deleteNodesOnly` should only be set to true when deleting
         * subjects in document reducer
         *
         * TODO: We should get rid of this block when breraking down this function to smaller pieces
         */
        if (!opts.deleteNodesOnly && Records.isEmpty(document.routeVertices)) {
            return { newNodes: [], selectionUpdates: [] };
        }
        /**
         * This method will be called from document reducer directly
         * The logic we use to update a net nodes are:
         * 1. Grab all the schematic vertices (note: not included nested vertices inside parts)
         *    and generate nets from them.
         * 2. Grab all Net nodes from the `_treeNodes` field which is all pcbLayoutNodes under document, and
         *    these nodes DO include nodes under sub-layout.
         * 3. Then we first remove nodes appear in `_treeNodes` but not appear in schematic nets
         * 4. Finally, we add net nodes that appear in schematic nets but not appear in `_treeNodes`
         *
         * This logic would remove all net nodes under a sub-layout whenever we updated the schematic vertices
         * object because `vertices` DOES NOT include vertices inside a part. So we add the condition
         * in `deleteNets` method to skip deleting nets that are under a sub-layout
         *
         * ```
         * getAncestorUidByType(deletedNetId, PcbNodeTypes.element)
         * ```
         *
         * TODO: - If `vertices` is updated to include all schematic vertices under both current doc and also under all
         * parts being used in the doc, then we should remove the condition in `deleteNets`. Otherwise, it will
         * break the logic
         *
         * TODO: Phase 2 of document nets - Move this call and the creation of
         * Document Nets to the a function in the reducer and and update this
         * method to be driven by Document Nets. This will mean we can drop the
         * diffing here and have document nets become the single source of truth
         * for nets.
         */
        const oldNets = getNetHashMap(document.nets);
        // Create a map of net hashes to net IDs in the document
        const docNetHashLookup = Object.fromEntries(Object.entries(document.nets).map(([id, net]) => [net.hash ?? id, id]));
        // Build this up front so we can reuse it for any new or split nets without having to rebuild
        const netDesignatorSequencer = createNetDesignatorSequencer(document.nets);
        const elements = { ...(document.subDocumentData?.elements ?? {}), ...document.elements };
        const routeVertices = { ...(document.subDocumentData?.routeVertices ?? {}), ...document.routeVertices };
        const generatedNets = generateNetInstanceMap(elements, routeVertices);
        // Just the top-level nets, and exclude their nested terminals.
        // Note: there may be some nets that have only subdocument terminals, i.e.
        // aren't connected to terminals in the host document. We filter these out here
        // (`length > 0`) because we only generate net objcts and pcb net nodes for
        // nets connected to host document terminals.
        const topLevelNets = chain(generatedNets)
            .pickBy((net) => net.topLevelTerminals().length > 0)
            .value();
        // TODO the entire algorithm below to merge/split nets is a bit suspect, imo. It doesn't use
        // a single source of truth for the nets, and instead modifies the document.nets and their
        // terminals based on a completely different algorithm to how the nets were generated in the
        // first place. And it's causing consistency bugs.
        //
        // Instead, we should take a different approach:
        //
        // 1. Use the wire nets (generatedNets) as the source of truth (since we effectively do anyway,
        // just badly) and create/delete all the correct document nets and pcb net nodes from those.
        // 2. Detect merged/split nets either as we do now, or perhaps with a slightly improved algorithm
        // (there are some potential improvements noted in their code), but either way producing a map
        // of which nets were merged/split.
        // 3. Use this list solely to update the already-valid nets, just copying the fields of the merge
        // winners etc as appropriate, not creating or deleting any more nets.
        // Get the diff between the new and old nets
        const { added, deleted, merged, split, unchanged } = diffNetMaps(oldNets, mapValues(topLevelNets, (n) => Net.terminalsToMap(n.topLevelTerminals())));
        // First, add new net nodes, if this document has a PCB. Best way to detect no PCB right
        // now is whether there's a top-level Layout or Footprint node.
        // (Note, some legacy documents don't have a PCB; we consider a schematic-only document
        // to be a valid document state, even though the current version of the app doesn't create
        // such documents, so we still want to support PCB-less projects)
        const topLevelContainer = getFirstTopLevelContainerNode(this.treeNodes);
        const hasPcb = Boolean(topLevelContainer);
        if (!hasPcb) {
            // Returning early here as we don't want to add any nets to a document without a PCB
            // and new nodes can be created while spliting as well as when adding new nets
            // TODO not adding to document.nets here leaves the document in a kinda broken state.
            // We should keep document.nets valid even if no pcb (and therefore no pcb net nodes).
            return { newNodes: [], selectionUpdates: [] };
        }
        // Keep track of any new nodes that are added so we can return them;
        const newNodes = this.addNets(document, pick(generatedNets, added), netDesignatorSequencer, opts.netNameHints);
        const selectionUpdates = newNodes.map((node) => node.uid);
        let netsContainer = getChildByName(this.treeNodes, topLevelContainer.uid, "Nets", this.logger);
        if (!netsContainer) {
            // Re-add all the unchanged net pcb nodes to the document, if the pcb `Nets` node is
            // non-existent or has been removed for some reason.
            // TODO why would this ever happen? Shouldn't the `Nets` node always exist if we've
            // ever added any nets above? Is this only to catch the first-time schematic-only scenario?
            netsContainer = this.getOrSetNetsNode(topLevelContainer.uid);
            const reCreatedNodes = unchanged.map((netHash) => {
                const netInstance = generatedNets[netHash];
                const terminals = netInstance.terminalsByDocLevel();
                // This should exist as it was used to determine the net was unchanged
                const net = document.nets[docNetHashLookup[netHash]];
                // Create the net node
                return createNetNode(net.uid, netsContainer.uid, net.label, terminals);
            });
            addPcbNodes(this.treeNodes, reCreatedNodes);
            newNodes.push(...reCreatedNodes);
        }
        else {
            // HACK: Supplement the diffNetMaps call above with a comparison of
            // submodule terminals, which are important for fills and other
            // features, and are are ignored by diffNetMaps.
            // QUESTION: Why not just make net hashes depend on submodule
            // terminals? and-or make nets contain submodule terminals? then we
            // can remove this block of code and simplify net PCB nodes by
            // removing terminals from it entirely!
            // TODO: FLUX-5059 Make net hashes depend on submodule terminals and-or make nets contain submodule terminals
            const netNodesHavingSubmoduleTerminalsChanged = compact(unchanged.map((netHash) => {
                // The new net should always exist as it was used to determine the net was unchanged above
                const net = document.nets[docNetHashLookup[netHash]];
                const newTerminals = generatedNets[netHash].terminalsByDocLevel();
                // HACK: The old net node does not necessarily exist even
                // when the corresponding net object is unchanged. Not sure
                // why.
                const oldNetNode = document.pcbLayoutNodes[net.uid];
                if (!isEqual(sortBy(newTerminals.submodule), sortBy(Object.keys(oldNetNode?.submoduleTerminals ?? {})))) {
                    return createNetNode(net.uid, netsContainer.uid, net.label, newTerminals);
                }
            }));
            addPcbNodes(this.treeNodes, netNodesHavingSubmoduleTerminalsChanged);
            newNodes.push(...netNodesHavingSubmoduleTerminalsChanged);
        }
        // Create new nets for any nets that have been split and add them to the document
        // one net gets to keep the ID of the original net, the rest get new IDs
        const { newSplitNodes, splitNetHashes } = splitNets(document, split, this.treeNodes, docNetHashLookup, netDesignatorSequencer, generatedNets, netsContainer.uid);
        newNodes.push(...newSplitNodes);
        // Remove any deleted nets
        // TODO FLUX-5090 I'm *fairly* sure now that with net merging/splitting (once its bugs are fixed), because
        // it gives us persistent nets, we can now just delete the entire subtree of `deleted` nets,
        // because we don't need to reparent traces into new net nodes. Test and think about further.
        deleteNetsExcludingDescendants(document, deleted.map((netHash) => docNetHashLookup[netHash]), this.treeNodes);
        // Handle nets that are merging together. Merging will not create new nodes
        // so do not add them to the newNodes list
        const mergeSelectionUpdates = mergeNets(document, merged, oldNets, this.treeNodes, docNetHashLookup, generatedNets, this.logger);
        newNodes.push(...this.ensureNetAndPcbNodesConsistency(document, topLevelNets, netDesignatorSequencer));
        selectionUpdates.push(...mergeSelectionUpdates);
        // TODO FLUX-5090 I'm *fairly* sure now that this whole section to reparent segments is
        // both useless (because we no longer have valid `startId` / `endId`) and no longer necessary
        // now that we have persistent nets. See above
        const { segmentsByTerminal, middleSegments } = this.initialiseTerminalMap();
        // TODO FLUX-5090 make the data flow clearer here by returning new values instead of mutating input params
        this.categorizeMiddleSegments(segmentsByTerminal, middleSegments);
        // After the nets have been added/removed, might have a new or pins, so we need to update them
        const modified = splitNetHashes.concat(Object.keys(merged));
        // Document nets is updated at this point, build a new map that can be used for re-parenting
        const updatedNetHashMap = getNetHashMap(document.nets);
        const modifiedNetsMap = modified.reduce((acc, netHash) => {
            const terminals = updatedNetHashMap[netHash];
            if (terminals) {
                acc[netHash] = terminals;
            }
            return acc;
        }, {});
        reParentSegments(modifiedNetsMap, segmentsByTerminal, this.treeNodes, Object.fromEntries(Object.entries(document.nets).map(([id, net]) => [net.hash, id])));
        /**
         * TODO: There are orphaned segments at this point because when deleting nets above,
         * we do not delete their descendants. See comment in deleteNetsExcludingDescendants().
         */
        removeOrphanedSegments(this.treeNodes);
        // Dev environment only because of the potential performance impact
        if (isDevEnv()) {
            // Sanity check that we have a 1:1 mapping between nets and net nodes
            // TODO this doesn't check both directions. Probably bad!
            if (!Object.keys(document.nets).every((netId) => this.treeNodes[netId] && this.treeNodes[netId].type === "net")) {
                const badNets = Object.values(document.nets).filter((n) => this.treeNodes[n.uid]?.type !== "net");
                // eslint-disable-next-line no-console
                console.error("Net nodes and nets are out of sync. Missing from PCB net nodes:", badNets.map((n) => n.label));
                throw new Error("Net nodes and nets are out of sync.");
            }
        }
        return { selectionUpdates, newNodes };
    }
    /** Get node, or if it doesn't exist, return a new one, without adding it to the tree. */
    getOrCreateSystemNode(name, parentUid, nodeType, nodeUid) {
        const existingNode = nodeUid ? this.treeNodes[nodeUid] : null;
        return existingNode ?? createSystemNode(nodeType, name, parentUid, { uid: nodeUid });
    }
    removeVirtualNodesOfFootprintAsset(footprintNode) {
        // Now this methods both removes nodes in-place, and also returns the nodes to be removed.
        // We need the in-place mutation in case follow-up tasks depends on the mutation. We then return
        // and remove the nodes from document service
        // TODO: [NodeType] Clean up node type here, and remove `as` cast, both should be baked node
        const childUidsToRemove = (footprintNode.childUids ?? []).filter((childUid) => {
            const node = this.treeNodes[childUid];
            if (node) {
                return isVirtualNodeGeneratedFromFootprintAssetRule(node, footprintNode);
            }
            else {
                if ("captureError" in this.logger) {
                    this.logger.captureError(new MissingChildUidError(`node ${footprintNode.uid} with type ${footprintNode.type}`));
                }
                else {
                    this.logger.error(`node ${footprintNode.uid} with type ${footprintNode.type}`);
                }
            }
            return false;
        });
        const nodesToRemove = [];
        for (const childUid of childUidsToRemove) {
            const subTree = getSubTreeUids({
                pcbNodes: this.treeNodes,
                nodeUid: childUid,
                includeRoot: true,
            }).map((uid) => this.treeNodes[uid]);
            nodesToRemove.push(...subTree);
        }
        removeNodesAndDescendants(this.treeNodes, childUidsToRemove);
        return nodesToRemove;
    }
    /** Ensure that there is a 1:1:1 mapping between wire nets, document nets, and pcb net nodes.
     * This function gets run after the merging and splitting of nets, as a mop-up to ensure that
     * any missed document nets or pcb net nodes are always covered, treating wire nets as the
     * source of truth.
     *
     * TODO this function is a rescue measure to cover for the fact that the updateNetNode->splitNets
     * and mergeNets functions are not implemented in a way to make definitively certain that only one
     * net / pcb net node will be created for each wire net (currently the source of truth). The real
     * fix here will be to restructure those functions to ensure a 1:1:1 mapping (per comment in
     * updateNetNodes).
     */
    ensureNetAndPcbNodesConsistency(document, wireNets, netDesignatorSequencer) {
        // TODO FLUX-5090 we should add a migration to ensure all document nets have a valid hash!
        // It'd make logic like this easier to reason robustly about.
        const docNetsByHash = Object.fromEntries(Object.values(document.nets).map((n) => [n.hash ?? n.uid, n]));
        if (Records.count(docNetsByHash) !== Records.count(document.nets)) {
            // If the by-hash map has fewer entries than the nets map, that means there are
            // duplicate hashes in the document nets, which is a bug! We need to fix that
            // at the source (some possible sources marked with TODOs in PR14105), but in
            // the meantime, and in order to ensure we haven't missed any sources, also
            // clean those nets up here.
            // TODO FLUX-5090: log unexpected conditions here, per Jeff's comment at
            // https://github.com/buildwithflux/flux-app/pull/14122#discussion_r1894124150
            const docNetsWithDupeHashes = chain(Object.values(document.nets))
                .groupBy((n) => n.hash ?? n.uid)
                .pickBy((nets) => nets.length > 1)
                .value();
            for (const [hash, nets] of Object.entries(docNetsWithDupeHashes)) {
                nets.sort(keySort((n) => -Records.count(n.pins)));
                // Keep the net with the most pins, and delete the rest.
                deleteNetsExcludingDescendants(document, nets.slice(1).map((n) => n.uid), this.treeNodes);
                // Ensure the main net has the right pins:
                const wireNet = wireNets[hash];
                if (!wireNet)
                    continue;
                const remainingNet = nets[0];
                remainingNet.pins = {};
                const { topLevel: topLevelPins, submodule: submodulePins } = wireNet.terminalsByDocLevel();
                addPinsToNet(document.nets, remainingNet.uid, topLevelPins);
                const pcbNetNode = PcbNodeHelper.getNodeOfType(this.treeNodes, remainingNet.uid, PcbNodeTypes.net);
                if (pcbNetNode) {
                    pcbNetNode.terminals = Net.terminalsToMap(topLevelPins);
                    pcbNetNode.submoduleTerminals = Net.terminalsToMap(submodulePins);
                }
            }
        }
        // I don't think we need to ensure terminals match here, because the hash
        // should ensure identical terminals. However, the current setup still feels a bit
        // fragile in that respect! Leaving as-is for now, because we don't have direct
        // evidence of known bugs from that cause, and I hope to improve the clarity
        // and robustness of the main net sync codepath sooner than later.
        // Add missing document nets that exist as wire nets.
        const newNodes = this.addNets(document, omit(wireNets, Object.keys(docNetsByHash)), netDesignatorSequencer);
        // Remove document nets that don't have wire nets (and their PCB net nodes)
        // TODO FLUX-5090 can probably now just delete net subtree, instead of orphaning traces. See comment at
        // other callsite.
        deleteNetsExcludingDescendants(document, Object.entries(docNetsByHash)
            .filter(([hash, _n]) => !(hash in wireNets))
            .map(([_hash, n]) => n.uid), this.treeNodes);
        // Now that we know document.nets is in sync with wire nets, ensure that there's
        // exactly 1 pcb node for each document net and vice versa (although the above
        // addNets/deleteNets blocks ensure this for any nets they add/delete, we still
        // need to ensure it for any nets updated previously)
        const topLevelContainer = getFirstTopLevelContainerNode(this.treeNodes);
        const netsContainer = getChildByName(this.treeNodes, topLevelContainer.uid, "Nets", this.logger);
        if (!netsContainer) {
            // We know that prior code to this function has already ensured these container nodes exist,
            // so fail hard here to assert that.
            throw new Error("ensureNetAndPcbNodesConsistency() called without first ensuring PCB Nets node exists");
        }
        // Add missing PCB Net nodes that exist as document nets
        const nodesToAdd = [];
        for (const [uid, docNet] of Object.entries(document.nets)) {
            if (uid in this.treeNodes)
                continue;
            const terminals = wireNets[docNet.hash ?? docNet.uid].terminalsByDocLevel();
            nodesToAdd.push(createNetNode(docNet.uid, netsContainer.uid, docNet.label, terminals));
        }
        addPcbNodes(this.treeNodes, nodesToAdd);
        newNodes.push(...nodesToAdd);
        // Remove PCB Net nodes that don't have document nets
        const pcbNetNodes = getDescendantsOfType(this.treeNodes, netsContainer, PcbNodeTypes.net);
        const unwantedPcbNetIds = pcbNetNodes.filter((n) => !(n.uid in document.nets)).map((n) => n.uid);
        // TODO FLUX-5090 see comment re deleting whole subtree, at callsite above.
        deleteNetsExcludingDescendants(document, unwantedPcbNetIds, this.treeNodes);
        return newNodes;
    }
    getOrSetSystemNode(name, parentUid, type) {
        const existingNode = getChildByName(this.treeNodes, parentUid, name, this.logger);
        if (existingNode) {
            return existingNode;
        }
        else {
            const systemNode = createSystemNode(type, name, parentUid);
            addPcbNodes(this.treeNodes, [systemNode]);
            return systemNode;
        }
    }
    getOrSetNetsNode(parentUid) {
        return this.getOrSetSystemNode("Nets", parentUid, PcbNodeTypes.container);
    }
    addNets(document, netsToAdd, netDesignatorSequencer, netNameHints) {
        if (Records.isEmpty(netsToAdd)) {
            // Nothing to do, but still allow calling this function because "add this list of zero nets"
            // is a valid operation, just doesn't change the doc. Keeps the callsite simpler.
            return [];
        }
        const topLevelContainer = getFirstTopLevelContainerNode(this.treeNodes);
        if (!topLevelContainer) {
            throw new Error("Adding Nets to project without any top level layout/footprint node");
        }
        const boardNetsNode = this.getOrSetNetsNode(topLevelContainer.uid);
        // Get the net name hints for all terminals in the document that may
        // exist from an imported document.
        const allNetNameHints = {};
        for (const element of Object.values(document.elements)) {
            for (const terminal of Object.values(element.part_version_data_cache.terminals)) {
                allNetNameHints[createTUID([element.uid, terminal.uid])] =
                    terminal.properties?.edif_net_name?.value.toString();
            }
        }
        Object.assign(allNetNameHints, netNameHints ?? {});
        const netNodes = Object.entries(netsToAdd).map(([addedNetHash, netInstance]) => {
            const terminals = netInstance.terminalsByDocLevel();
            // We don't have a prev net to get the name from :(
            const name = getNetName(Net.terminalsToMap(terminals.topLevel), document.elements, netDesignatorSequencer, allNetNameHints);
            const documentNet = addNet(document.nets, addedNetHash, name, terminals.topLevel);
            return createNetNode(documentNet.uid, boardNetsNode.uid, name, terminals);
        });
        addPcbNodes(this.treeNodes, netNodes);
        return netNodes;
    }
    /*
     * Creates a mapping of terminal ID's to their adjacent segments
     */
    initialiseTerminalMap() {
        const segmentsByTerminal = new Map();
        const middleSegments = new Set();
        const currentRouteSegmentNodes = pickBy(this.treeNodes, (n) => n.type === PcbNodeTypes.routeSegment);
        const isTerminal = (id) => {
            return !!this.treeNodes[id] && this.treeNodes[id].type !== PcbNodeTypes.via;
        };
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.values(currentRouteSegmentNodes).forEach((segment) => {
            let isConnectedToTerminal = false;
            if (isTerminal(segment.startId)) {
                isConnectedToTerminal = true;
                addToMapOfSets(segmentsByTerminal, segment.startId, segment);
            }
            if (isTerminal(segment.endId)) {
                isConnectedToTerminal = true;
                addToMapOfSets(segmentsByTerminal, segment.endId, segment);
            }
            if (!isConnectedToTerminal) {
                middleSegments.add(segment);
            }
        });
        return { segmentsByTerminal, middleSegments };
    }
    /*
     * If a segment is not immediately adjacent to a terminalId, store it alongside a segment that _is_
     */
    categorizeMiddleSegments(segmentsByTerminal, middleSegments) {
        // Denormalize the terminalMap to get a mapping of segment ID's to their terminal ID
        const knownVertices = new Map();
        for (const [terminalId, segments] of segmentsByTerminal.entries()) {
            for (const segment of segments) {
                // Note: when startId/endId point to other segments (as opposed to terminals), the startId/endId
                // values are not the uids of actual segments; afaict they are arbitrary uids that point to nothing
                // but each other. Hence we add startId/endId to the map here, not segment.uid
                knownVertices.set(segment.startId, terminalId);
                knownVertices.set(segment.endId, terminalId);
            }
        }
        let lastSize = Infinity;
        while (middleSegments.size < lastSize) {
            lastSize = middleSegments.size;
            for (const segment of middleSegments) {
                // Use the knownVertices map to find a terminal ID that this segment is adjacent to
                if (knownVertices.has(segment.startId) || knownVertices.has(segment.endId)) {
                    const terminalId = knownVertices.get(segment.startId) ?? knownVertices.get(segment.endId);
                    knownVertices.set(segment.startId, terminalId);
                    knownVertices.set(segment.endId, terminalId);
                    addToMapOfSets(segmentsByTerminal, terminalId, segment);
                    middleSegments.delete(segment);
                }
            }
        }
    }
}
function addToMapOfSets(map, key, value) {
    map.set(key, map.has(key) ? map.get(key).add(value) : new Set([value]));
}
function isVirtualNodeGeneratedFromFootprintAssetRule(node, originNode) {
    if (!PcbNodeHelper.isVirtualNode(node))
        return false;
    const virtualNodeInfo = node.virtualNodeInfo;
    if (virtualNodeInfo) {
        return virtualNodeInfo.originNodeUid === originNode.uid && virtualNodeInfo.originRule === "footprintAsset";
    }
    return false;
}
export default PcbTreeManager;
//# sourceMappingURL=PcbTreeManager.js.map